import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useEffect, useRef, useState } from "react";

import { useImagePostModalStore } from "../../store";
import { EmptyImages } from "./EmptyImages";
import { ImageOptionsDropdown } from "./ImageOptionsDropDown";

const IMAGE_STAGE_CLASS_NAME =
  "flex w-full items-center justify-center overflow-hidden lg:h-auto lg:min-h-0 lg:flex-1";

interface MainImageProps {
  currentImageIndex: number;
}
export const MainImage = ({ currentImageIndex }: MainImageProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [stageAspectRatio, setStageAspectRatio] = useState<number>();
  const stageRef = useRef<HTMLDivElement>(null);
  const { uploadedImages, galleryAspectRatio, setGalleryAspectRatio } =
    useImagePostModalStore();
  const currentImage = uploadedImages[currentImageIndex];
  const currentImageAspectRatio = currentImage
    ? currentImage.width / currentImage.height
    : undefined;

  useEffect(() => {
    if (!galleryAspectRatio && currentImageAspectRatio) {
      setGalleryAspectRatio(currentImageAspectRatio);
    }
  }, [galleryAspectRatio, currentImageAspectRatio, setGalleryAspectRatio]);

  useEffect(() => {
    setIsLoading(true);
  }, [currentImage?.url]);

  useEffect(() => {
    const stageElement = stageRef.current;
    if (!stageElement) return;

    const updateStageAspectRatio = () => {
      const { width, height } = stageElement.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setStageAspectRatio(width / height);
      }
    };

    updateStageAspectRatio();
    const resizeObserver = new ResizeObserver(updateStageAspectRatio);
    resizeObserver.observe(stageElement);

    return () => resizeObserver.disconnect();
  }, []);

  if (!currentImage) {
    return (
      <div className={IMAGE_STAGE_CLASS_NAME}>
        <EmptyImages />
      </div>
    );
  }

  const imageAspectRatio = currentImageAspectRatio ?? 1;
  const shouldConstrainByWidth = stageAspectRatio
    ? imageAspectRatio >= stageAspectRatio
    : imageAspectRatio >= 1;

  return (
    <div ref={stageRef} className={IMAGE_STAGE_CLASS_NAME}>
      <div
        className={classNames("relative max-h-full max-w-full", {
          "w-full": shouldConstrainByWidth,
          "h-full": !shouldConstrainByWidth,
        })}
        style={{ aspectRatio: imageAspectRatio }}
      >
        {!isLoading && <ImageOptionsDropdown currentImage={currentImage} />}
        <img
          loading="eager"
          src={currentImage.url}
          alt={currentImage.filename}
          onLoad={() => setIsLoading(false)}
          className={classNames("block size-full rounded-md object-contain", {
            invisible: isLoading,
          })}
        />
      </div>
    </div>
  );
};
